import * as React from 'react';
import { ActionSource, getSelectedProjectId } from 'owa-timestream-store';
import { getAssetDescriptor } from 'owa-timestream-asset-sdk';
import type { AssetDescriptor, NewOrExistingAsset } from 'owa-timestream-asset-sdk-types';
import type { DragData } from 'owa-dnd';
import createAssetFromDescriptor from './createAssetFromDescriptor';
import createAssetOnPageCoordinates from './createAssetOnPageCoordinates';
import moveAssetToPageCoordinates from './moveAssetToPageCoordinates';

// Two types of drag items are supported:
// TimeStreamAsset -> itemData must contain a NewOrExistingAsset object (used by activity panel, previously used by toolbox)
// TimeStreamAssetType -> itemData contains the type of an AssetDescriptor (used by toolbox)
// Keep in mind drag data gets JSON-serialized, so we can't send the AssetDescriptor itself, for example.
const TimeStreamAsset = 'TimeStreamAsset';
const TimeStreamAssetType = 'TimeStreamAssetType';

type AssetDragData = DragData & {
    actionSource: ActionSource;
};

export function useGetAssetDragData(assetDescriptor: AssetDescriptor, actionSource: ActionSource) {
    return React.useCallback(() => {
        return {
            itemType: TimeStreamAssetType,
            itemData: assetDescriptor.type,
            actionSource: actionSource,
        };
    }, [assetDescriptor.type, actionSource]);
}

export function canDropAsset(dragData: DragData) {
    return dragData.itemType === TimeStreamAsset || dragData.itemType === TimeStreamAssetType;
}

export function onDropAsset(dragData: DragData, pageX: number, pageY: number) {
    const { itemType, itemData, actionSource } = dragData as AssetDragData;

    switch (itemType) {
        case TimeStreamAssetType:
            const assetDescriptor = getAssetDescriptor(itemData as string);
            if (assetDescriptor) {
                createAssetFromDescriptor(actionSource, assetDescriptor, pageX, pageY, 0, 0);
            }
            break;

        case TimeStreamAsset:
            const asset = itemData as NewOrExistingAsset;

            if (asset.existingAssetId) {
                // if the asset already exists, move it to the new location (ex: items from captured panel)
                moveAssetToPageCoordinates(
                    pageX,
                    pageY,
                    getSelectedProjectId(),
                    asset.existingAssetId
                );
            } else {
                // create new assets with pre-populated payload (ex: items from search results)
                createAssetOnPageCoordinates(pageX, pageY, asset, actionSource);
            }
            break;

        // $TODO: support other drag-and-drop types, like Files
    }
}
